import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { updateCustomer } from '../api';
import { toApiError } from '../errors';
import { useJourney } from '../journey';
import { ILendingCustomer } from '../types';
import Button from '../ui/Button';
import { DateField, TextArea, TextField } from '../ui/Fields';
import { Notice, SectionTitle } from '../ui/Parts';
import Shell from '../ui/Shell';

/** The programme's customer fields (CustomerField) as form fields. */
const FIELDS: Record<string, keyof ILendingCustomer> = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  DATE_OF_BIRTH: 'dateOfBirth',
  EMAIL_ADDRESS: 'email',
  PHONE_NUMBER: 'phoneNumber',
  BVN: 'bvn',
  NIN: 'nin',
  ADDRESS: 'address',
};

/** A prefilled identity number comes back masked (*******1234); sent back unchanged, it means "keep it". */
const MASKED = /^\*+\d{4}$/;

const identityNumber = (label: string) =>
  Yup.string().test('identity', `${label} must be 11 digits`, (value) => !value || MASKED.test(value) || /^\d{11}$/.test(value));

const RULES: Record<keyof ILendingCustomer, Yup.StringSchema> = {
  firstName: Yup.string().trim(),
  lastName: Yup.string().trim(),
  dateOfBirth: Yup.string().test('past', 'Date of birth must be in the past', (value) => !value || new Date(value) < new Date()),
  email: Yup.string().trim().email('Enter a valid email address'),
  phoneNumber: Yup.string().trim().matches(/^\+?\d{10,14}$/, { message: 'Enter a valid phone number', excludeEmptyString: true }),
  bvn: identityNumber('BVN'),
  nin: identityNumber('NIN'),
  address: Yup.string().trim(),
};

const YourInformation = () => {
  const { steps, current, session, setSession, updateUi, close } = useJourney();
  const [error, setError] = useState('');

  const requirement = (field: keyof ILendingCustomer) => {
    const entry = Object.entries(FIELDS).find(([, name]) => name === field);
    return session.fieldRequirements.find((r) => r.field === entry?.[0])?.requirement ?? 'NOT_REQUIRED';
  };
  const shown = (field: keyof ILendingCustomer) => requirement(field) !== 'NOT_REQUIRED';
  const optional = (field: keyof ILendingCustomer) => requirement(field) === 'OPTIONAL';

  const fields = (Object.values(FIELDS) as (keyof ILendingCustomer)[]).filter(shown);
  const initialValues = Object.fromEntries(fields.map((f) => [f, session.customer?.[f] ?? ''])) as ILendingCustomer;
  const schema = Yup.object(
    Object.fromEntries(fields.map((f) => [f, optional(f) ? RULES[f] : RULES[f].required('Required')])),
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) => {
        setError('');
        try {
          const update = Object.fromEntries(
            Object.entries(values).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]).filter(([, v]) => v !== ''),
          ) as ILendingCustomer;
          setSession(await updateCustomer(update));
          updateUi({ detailsConfirmed: true, editingDetails: false });
        } catch (e) {
          setError(toApiError(e).message);
        }
      }}
    >
      {({ isSubmitting, submitForm }) => (
        <Shell
          steps={steps}
          current={current}
          onClose={close}
          footer={
            <div className="grid grid-cols-2 gap-4">
              <Button variant="secondary" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={submitForm} loading={isSubmitting}>
                Continue
              </Button>
            </div>
          }
        >
          <SectionTitle title="Your Information" subtitle="Provide your details to continue." />
          {error && <Notice>{error}</Notice>}
          <Form noValidate className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
            {shown('firstName') && <TextField name="firstName" label="First Name" placeholder="Enter first name" optional={optional('firstName')} />}
            {shown('lastName') && <TextField name="lastName" label="Last Name" placeholder="Enter last name" optional={optional('lastName')} />}
            {shown('dateOfBirth') && (
              <div className="md:col-span-2">
                <DateField name="dateOfBirth" label="Date of Birth" optional={optional('dateOfBirth')} />
              </div>
            )}
            {shown('email') && <TextField name="email" label="Email Address" type="email" inputMode="email" placeholder="Enter email address" optional={optional('email')} />}
            {shown('phoneNumber') && <TextField name="phoneNumber" label="Phone Number" type="tel" inputMode="tel" placeholder="Enter phone number" optional={optional('phoneNumber')} />}
            {shown('bvn') && <TextField name="bvn" label="BVN" inputMode="numeric" placeholder="Enter BVN" optional={optional('bvn')} />}
            {shown('nin') && <TextField name="nin" label="NIN" inputMode="numeric" placeholder="Enter NIN" optional={optional('nin')} />}
            {shown('address') && (
              <div className="md:col-span-2">
                <TextArea name="address" label="Residential Address" placeholder="Enter residential address" optional={optional('address')} />
              </div>
            )}
          </Form>
        </Shell>
      )}
    </Formik>
  );
};

export default YourInformation;
